import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, ArrowRight, ArrowLeft, Check, ShieldCheck, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../hooks/useAuth";
import { createPreviewRequest, previewCheckout } from "../lib/api";

const NOTICE =
  "LaunchBS Preview Projects provide a designed and functional starter preview of your idea. This package is not a complete production application or app-store-ready product.";

const Field = ({ label, required, children }) => (
  <div>
    <label className="block text-sm font-medium text-[#0F0F0F] mb-1.5">
      {label} {required && <span className="text-[#D4AF37]">*</span>}
    </label>
    {children}
  </div>
);

const inputCls =
  "w-full rounded-xl border border-gray-200 px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-[#D4AF37] bg-white";

const CATEGORY_OPTIONS = [
  "AI Platform", "Marketplace", "Healthcare", "Education", "Finance", "Creator Economy",
  "Agriculture", "Local Business", "SaaS", "E-Commerce", "Productivity", "Real Estate",
  "Future Economy", "Social Platform", "Other",
];
const DESIGN_STYLE_OPTIONS = [
  "Premium", "Modern", "Apple Inspired", "Luxury", "Minimal", "Dark", "Light", "Creative", "Futuristic",
];
const COLOR_OPTIONS = [
  "Black & Gold", "Blue & White", "Green", "Purple", "White & Silver",
  "Dark & Neon", "Pastel", "Monochrome",
];

const Dropdown = ({ value, onChange, options, placeholder = "Select...", testid }) => (
  <div className="relative">
    <select
      value={value}
      onChange={onChange}
      className={`${inputCls} appearance-none pr-9 cursor-pointer ${value ? "text-[#0F0F0F]" : "text-[#9CA3AF]"}`}
      data-testid={testid}
    >
      <option value="">{placeholder}</option>
      {options.map((o) => (
        <option key={o} value={o} className="text-[#0F0F0F]">{o}</option>
      ))}
    </select>
    <ChevronDown size={16} className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-[#9CA3AF]" />
  </div>
);

const empty = {
  project_name: "",
  business_name: "",
  customer_email: "",
  phone: "",
  app_category: "",
  idea_description: "",
  problem_statement: "",
  target_users: "",
  requested_features: "",
  design_style: "",
  preferred_colors: "",
  reference_links: "",
  additional_notes: "",
  agreed: false,
};

export const PreviewProjectModal = ({ open, onClose, onSubmitted }) => {
  const { user } = useAuth();
  const [step, setStep] = useState("form"); // form | review
  const [form, setForm] = useState({ ...empty, customer_email: user?.email || "" });
  const [submitting, setSubmitting] = useState(false);

  const set = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e?.target ? (e.target.type === "checkbox" ? e.target.checked : e.target.value) : e }));

  const canReview = form.project_name.trim() && form.idea_description.trim() && form.agreed;

  const toReview = () => {
    if (!canReview) {
      toast.error("Please complete the required fields and accept the terms.");
      return;
    }
    setStep("review");
  };

  const submitForReview = async () => {
    setSubmitting(true);
    try {
      await createPreviewRequest(form);
      toast.success("Request submitted for review", {
        description: "Our team will review your idea and get back to you shortly.",
      });
      setForm({ ...empty, customer_email: user?.email || "" });
      setStep("form");
      onSubmitted?.();
      onClose();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Could not submit your request. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const close = () => {
    setStep("form");
    onClose();
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={close}
          data-testid="preview-project-modal"
        >
          <motion.div
            className="bg-white rounded-3xl w-full max-w-2xl max-h-[92vh] overflow-y-auto"
            initial={{ scale: 0.96, y: 12 }}
            animate={{ scale: 1, y: 0 }}
            exit={{ scale: 0.96, y: 12 }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="sticky top-0 bg-white border-b border-gray-100 px-7 py-5 flex items-center justify-between z-10">
              <div>
                <h2 className="font-display text-xl font-bold text-[#0F0F0F]">
                  {step === "form" ? "Request a Preview Project" : "Review your project"}
                </h2>
                <p className="text-xs text-[#687280] mt-0.5">
                  {step === "form" ? "Tell us about your idea" : "Confirm details before submitting for review"}
                </p>
              </div>
              <button onClick={close} className="text-[#9CA3AF] hover:text-[#0F0F0F]" data-testid="preview-modal-close">
                <X size={22} />
              </button>
            </div>

            {step === "form" ? (
              <div className="p-7 space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <Field label="Project name" required>
                    <input className={inputCls} value={form.project_name} onChange={set("project_name")} data-testid="pp-project-name" />
                  </Field>
                  <Field label="Founder / business name">
                    <input className={inputCls} value={form.business_name} onChange={set("business_name")} data-testid="pp-business-name" />
                  </Field>
                  <Field label="Email address">
                    <input className={inputCls} type="email" value={form.customer_email} onChange={set("customer_email")} data-testid="pp-email" />
                  </Field>
                  <Field label="Phone number (optional)">
                    <input className={inputCls} value={form.phone} onChange={set("phone")} data-testid="pp-phone" />
                  </Field>
                </div>
                <Field label="App or business idea" required>
                  <textarea rows={3} className={inputCls} value={form.idea_description} onChange={set("idea_description")} data-testid="pp-idea" />
                </Field>
                <Field label="Problem the product solves">
                  <textarea rows={2} className={inputCls} value={form.problem_statement} onChange={set("problem_statement")} data-testid="pp-problem" />
                </Field>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <Field label="Category / type">
                    <Dropdown value={form.app_category} onChange={set("app_category")} options={CATEGORY_OPTIONS} placeholder="Select a category" testid="pp-category" />
                  </Field>
                  <Field label="Target users">
                    <input className={inputCls} value={form.target_users} onChange={set("target_users")} data-testid="pp-target" />
                  </Field>
                </div>
                <Field label="Main features requested">
                  <textarea rows={2} className={inputCls} value={form.requested_features} onChange={set("requested_features")} data-testid="pp-features" />
                </Field>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <Field label="Preferred design style">
                    <Dropdown value={form.design_style} onChange={set("design_style")} options={DESIGN_STYLE_OPTIONS} placeholder="Select a style" testid="pp-style" />
                  </Field>
                  <Field label="Brand colors (optional)">
                    <Dropdown value={form.preferred_colors} onChange={set("preferred_colors")} options={COLOR_OPTIONS} placeholder="Select colors" testid="pp-colors" />
                  </Field>
                </div>
                <Field label="Example apps or websites">
                  <input className={inputCls} value={form.reference_links} onChange={set("reference_links")} data-testid="pp-refs" />
                </Field>
                <Field label="Additional instructions">
                  <textarea rows={2} className={inputCls} value={form.additional_notes} onChange={set("additional_notes")} data-testid="pp-notes" />
                </Field>

                <div className="rounded-xl bg-[#F7F7F7] p-4 text-xs text-[#687280] leading-relaxed">{NOTICE}</div>

                <label className="flex items-start gap-3 cursor-pointer">
                  <input type="checkbox" checked={form.agreed} onChange={set("agreed")} className="mt-1 accent-[#D4AF37]" data-testid="pp-agree" />
                  <span className="text-sm text-[#0F0F0F]">
                    I understand this is a starter preview package, not a complete production application, and I accept the terms.
                  </span>
                </label>

                <button
                  onClick={toReview}
                  disabled={!canReview}
                  className="w-full inline-flex items-center justify-center gap-2 bg-[#D4AF37] text-[#0F0F0F] rounded-full py-3.5 text-sm font-semibold hover:bg-[#c5a030] transition-colors disabled:opacity-40"
                  data-testid="pp-review-btn"
                >
                  Review Request <ArrowRight size={16} />
                </button>
              </div>
            ) : (
              <div className="p-7 space-y-5">
                <div className="space-y-3">
                  <Row label="Project name" value={form.project_name} />
                  {form.app_category && <Row label="Category" value={form.app_category} />}
                  <Row label="Idea summary" value={form.idea_description} />
                  {form.requested_features && <Row label="Requested features" value={form.requested_features} />}
                  {(form.design_style || form.preferred_colors) && (
                    <Row label="Design preferences" value={[form.design_style, form.preferred_colors].filter(Boolean).join(" · ")} />
                  )}
                </div>

                <div className="rounded-2xl border border-gray-100 p-5">
                  <p className="font-display font-bold text-[#0F0F0F] mb-3">Package contents</p>
                  <ul className="space-y-2 text-sm text-[#0F0F0F]">
                    {["Interactive designed preview of your idea", "AI Builder Prompt", "Feature blueprint & documentation", "One revision round included"].map((f) => (
                      <li key={f} className="flex items-center gap-2">
                        <Check size={15} className="text-[#D4AF37]" /> {f}
                      </li>
                    ))}
                  </ul>
                  <div className="flex items-center justify-between mt-5 pt-4 border-t border-gray-100">
                    <div>
                      <p className="text-sm text-[#687280]">One-time price (after approval)</p>
                      <p className="text-xs text-[#9CA3AF]">Estimated delivery: 7–10 business days</p>
                    </div>
                    <span className="font-display text-2xl font-extrabold text-[#0F0F0F]" data-testid="pp-price">$250</span>
                  </div>
                </div>

                <div className="rounded-xl bg-[#FFFDF5] border border-[#D4AF37]/30 p-4 text-sm text-[#0F0F0F] leading-relaxed" data-testid="pp-review-notice">
                  <span className="font-semibold">No payment required yet.</span> Submit your request and our team will
                  review it. Once approved, a secure payment option will appear on your project card in your profile.
                </div>

                <div className="flex items-center gap-3">
                  <button
                    onClick={() => setStep("form")}
                    className="inline-flex items-center gap-2 rounded-full border border-gray-200 px-5 py-3 text-sm font-semibold text-[#0F0F0F] hover:bg-[#F7F7F7] transition-colors"
                    data-testid="pp-back-btn"
                  >
                    <ArrowLeft size={16} /> Back
                  </button>
                  <button
                    onClick={submitForReview}
                    disabled={submitting}
                    className="flex-1 inline-flex items-center justify-center gap-2 bg-[#D4AF37] text-[#0F0F0F] rounded-full py-3.5 text-sm font-semibold hover:bg-[#c5a030] transition-colors disabled:opacity-60"
                    data-testid="pp-submit-btn"
                  >
                    {submitting ? "Submitting…" : <>Submit Request for Review <ArrowRight size={16} /></>}
                  </button>
                </div>
                <p className="flex items-center justify-center gap-1.5 text-xs text-[#687280]">
                  <ShieldCheck size={13} /> Reviewed by the LaunchBS team · No charge until approved
                </p>
              </div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

const Row = ({ label, value }) => (
  <div className="flex gap-4">
    <span className="text-sm text-[#9CA3AF] w-36 shrink-0">{label}</span>
    <span className="text-sm text-[#0F0F0F] flex-1">{value}</span>
  </div>
);
